import type { Upload } from "@acervo/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { CheckCircle2, FileVideo, Loader2, Play, RotateCcw, UploadCloud, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type DragEvent } from "react";
import { Empty, ErrorBox, PageHeader, ProgressBar, Spinner } from "../../components/ui";
import { api, useSession } from "../../lib/api";
import { errorMessage, relative } from "../../lib/format";
import { naturalCompare, titleFromFilename, type Destination, type Job } from "../../lib/upload-queue";
import { uploadQueue } from "../../lib/uploads";

const route = getRouteApi("/_app/console/envios");
const NEW = "__novo__";
const VIDEO_ACCEPT = "video/*,.mkv,.avi,.wmv,.flv,.ts,.mpg,.mpeg";

export function UploadsPage() {
  useLeaveWarning();
  return (
    <div className="space-y-8">
      <PageHeader
        title="Enviar aulas"
        subtitle="Arraste os vídeos já editados. O servidor converte e a aula aparece no curso sozinha, em alguns minutos."
      />
      <NewBatch />
      <ActiveQueue />
      <History />
    </div>
  );
}

// ------------------------------------------------------------ nova remessa

interface Picked {
  file: File;
  lessonTitle: string;
}

function NewBatch() {
  const { curso } = route.useSearch();
  const session = useSession()!;
  const isOwner = session.user.role === "owner";
  const courses = useQuery({ queryKey: ["admin", "courses"], queryFn: () => api.unwrap(api.client.GET("/v1/courses")) });

  const [courseId, setCourseId] = useState<string>(curso ?? "");
  const [newTitle, setNewTitle] = useState("");
  const [owned, setOwned] = useState(true);
  const [moduleTitle, setModuleTitle] = useState("");
  const [files, setFiles] = useState<Picked[]>([]);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const sorted = useMemo(
    () => [...(courses.data?.items ?? [])].sort((a, b) => a.title.localeCompare(b.title, "pt-BR")),
    [courses.data],
  );
  const selected = sorted.find((c) => c.id === courseId);

  // Módulos do curso escolhido, para sugerir no campo.
  const detail = useQuery({
    queryKey: ["course", selected?.slug],
    queryFn: () => api.unwrap(api.client.GET("/v1/courses/{slug}", { params: { path: { slug: selected!.slug } } })),
    enabled: !!selected,
  });
  const modules = detail.data?.modules.map((m) => m.title) ?? [];
  useEffect(() => {
    if (courseId === NEW) setModuleTitle((t) => t || "Módulo 1");
    else if (modules.length) setModuleTitle((t) => (t && modules.includes(t) ? t : modules[modules.length - 1]!));
  }, [courseId, detail.data]);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const vids = [...list].filter((f) => f.type.startsWith("video/") || /\.(mkv|avi|wmv|flv|ts|mpe?g|mov|m4v|webm|mp4)$/i.test(f.name));
    vids.sort((a, b) => naturalCompare(a.name, b.name));
    setFiles((cur) => [...cur, ...vids.map((file) => ({ file, lessonTitle: titleFromFilename(file.name) }))]);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    addFiles(e.dataTransfer.files);
  };

  const dest: Destination | null =
    courseId === NEW
      ? newTitle.trim()
        ? { newCourse: { title: newTitle.trim(), owned: isOwner ? owned : true } }
        : null
      : courseId
        ? { courseId }
        : null;
  const valid = dest && moduleTitle.trim() && files.length > 0 && files.every((f) => f.lessonTitle.trim());
  const total = files.reduce((s, f) => s + f.file.size, 0);

  const start = () => {
    if (!dest || !valid) return;
    uploadQueue.add(
      files.map((f) => ({ file: f.file, lessonTitle: f.lessonTitle.trim() })),
      dest,
      moduleTitle.trim(),
      courseId === NEW ? newTitle.trim() : (selected?.title ?? ""),
    );
    setFiles([]);
  };

  if (courses.isPending) return <Spinner />;
  if (courses.isError) return <ErrorBox error={courses.error} />;

  return (
    <section className="card space-y-5 p-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="label" htmlFor="up-course">
            Curso
          </label>
          <select id="up-course" className="field" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">Escolha…</option>
            <option value={NEW}>+ Criar curso novo</option>
            {sorted.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
                {c.rights === "third_party" ? " (pessoal)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="up-module">
            Módulo
          </label>
          <input
            id="up-module"
            className="field"
            list="up-modules"
            placeholder="Ex.: Módulo 1"
            value={moduleTitle}
            onChange={(e) => setModuleTitle(e.target.value)}
          />
          <datalist id="up-modules">
            {modules.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <p className="mt-1 text-xs text-muted">Escolha um módulo existente ou digite um nome novo.</p>
        </div>
        {courseId === NEW && (
          <>
            <div>
              <label className="label" htmlFor="up-new">
                Nome do curso novo
              </label>
              <input id="up-new" className="field" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            </div>
            <div className="flex items-end">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 accent-accent"
                  checked={isOwner ? owned : true}
                  disabled={!isOwner}
                  onChange={(e) => setOwned(e.target.checked)}
                />
                <span>
                  Este curso é meu
                  <span className="block text-xs text-muted">
                    Poderá ser publicado para alunos e vendido. Desmarcado, fica pessoal: só você vê.
                  </span>
                </span>
              </label>
            </div>
          </>
        )}
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          drag ? "border-accent bg-accent/5" : "border-line hover:border-accent/60"
        }`}
      >
        <UploadCloud className="h-8 w-8 text-accent" aria-hidden />
        <p className="font-semibold">Arraste os vídeos aqui ou clique para escolher</p>
        <p className="text-xs text-muted">
          MP4 (H.264) é o mais rápido de processar. Pode enviar vários de uma vez: entram na ordem dos nomes.
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={VIDEO_ACCEPT}
          className="hidden"
          aria-label="Escolher vídeos"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {files.length > 0 && (
        <ol className="divide-y divide-line rounded-lg border border-line">
          {files.map((f, i) => (
            <li key={`${f.file.name}-${i}`} className="flex items-center gap-3 px-3 py-2">
              <FileVideo className="h-4 w-4 shrink-0 text-muted" aria-hidden />
              <input
                className="field flex-1 py-1"
                aria-label={`Título da aula ${i + 1}`}
                value={f.lessonTitle}
                onChange={(e) =>
                  setFiles((cur) => cur.map((x, j) => (j === i ? { ...x, lessonTitle: e.target.value } : x)))
                }
              />
              <span className="hidden w-48 truncate text-xs text-muted sm:block" title={f.file.name}>
                {f.file.name}
              </span>
              <span className="w-20 text-right font-mono text-xs text-muted">{bytes(f.file.size)}</span>
              <button
                className="rounded p-1 text-muted hover:text-danger"
                aria-label={`Remover ${f.file.name}`}
                onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ol>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn-primary" disabled={!valid} onClick={start}>
          <UploadCloud className="h-4 w-4" />
          {files.length > 1 ? `Enviar ${files.length} aulas` : "Enviar aula"}
          {files.length > 0 && <span className="font-normal">· {bytes(total)}</span>}
        </button>
        {!dest && <span className="text-xs text-muted">Escolha o curso.</span>}
      </div>
    </section>
  );
}

// ------------------------------------------------------------ enviando agora

function ActiveQueue() {
  const jobs = useSyncExternalStore(uploadQueue.subscribe, uploadQueue.getSnapshot);
  const qc = useQueryClient();
  const sentCount = jobs.filter((j) => j.state === "sent").length;
  useEffect(() => {
    if (sentCount > 0) void qc.invalidateQueries({ queryKey: ["admin", "uploads"] });
  }, [sentCount, qc]);

  if (jobs.length === 0) return null;
  const finished = jobs.every((j) => j.state === "sent" || j.state === "error" || j.state === "canceled");
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-bold">Enviando deste computador</h2>
        {finished && (
          <button className="text-sm text-accent hover:underline" onClick={() => uploadQueue.clearFinished()}>
            Limpar
          </button>
        )}
      </div>
      <ul className="card divide-y divide-line">
        {jobs.map((j) => (
          <JobRow key={j.key} job={j} />
        ))}
      </ul>
      {!finished && (
        <p className="mt-2 text-xs text-muted">
          Não feche esta aba até terminar. Pode navegar pelo console. Se fechar, dá para continuar depois pelo histórico.
        </p>
      )}
    </section>
  );
}

const jobLabel: Record<Job["state"], string> = {
  waiting: "Aguardando",
  sending: "Enviando",
  finishing: "Finalizando",
  sent: "Enviado ✓ (conversão no histórico abaixo)",
  error: "Erro",
  canceled: "Cancelado",
};

function JobRow({ job: j }: { job: Job }) {
  const pct = Math.round((j.sentBytes / Math.max(j.file.size, 1)) * 100);
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{j.lessonTitle}</p>
        <p className="truncate text-xs text-muted">
          {j.courseLabel} › {j.moduleTitle}
        </p>
        {(j.state === "sending" || j.state === "finishing") && <ProgressBar value={pct} className="mt-2" />}
        {j.error && <p className="mt-1 text-xs text-danger">{j.error}</p>}
      </div>
      <span className={`text-xs ${j.state === "error" ? "text-danger" : j.state === "sent" ? "text-ok" : "text-muted"}`}>
        {jobLabel[j.state]}
        {j.state === "sending" && ` ${pct}%`}
      </span>
      {(j.state === "waiting" || j.state === "sending") && (
        <button className="rounded p-1 text-muted hover:text-danger" aria-label="Cancelar" onClick={() => void uploadQueue.cancel(j.key)}>
          <X className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

// ------------------------------------------------------------ histórico

function History() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["admin", "uploads"],
    queryFn: () => api.unwrap(api.client.GET("/v1/admin/uploads")),
    // Enquanto algo converte, atualiza a cada 3 s.
    refetchInterval: (q) =>
      q.state.data?.items.some((u) => u.status === "queued" || u.status === "processing") ? 3000 : 30_000,
  });
  const lastDone = list.data?.items.find((u) => u.status === "done")?.id;
  useEffect(() => {
    if (lastDone) {
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["admin", "courses"] });
    }
  }, [lastDone, qc]);

  return (
    <section>
      <h2 className="mb-3 text-lg font-bold">Histórico</h2>
      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="Nenhum envio ainda">As aulas enviadas por aqui aparecem nesta lista com o andamento da conversão.</Empty>
      ) : (
        <ul className="card divide-y divide-line">
          {list.data.items.map((u) => (
            <HistoryRow key={u.id} u={u} />
          ))}
        </ul>
      )}
    </section>
  );
}

function HistoryRow({ u }: { u: Upload }) {
  const qc = useQueryClient();
  const jobs = useSyncExternalStore(uploadQueue.subscribe, uploadQueue.getSnapshot);
  const sendingHere = jobs.some((j) => j.upload?.id === u.id && (j.state === "sending" || j.state === "finishing" || j.state === "waiting"));
  const pick = useRef<HTMLInputElement>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["admin", "uploads"] });

  const retry = useMutation({
    mutationFn: () => api.unwrap(api.client.POST("/v1/admin/uploads/{id}/retry", { params: { path: { id: u.id } } })),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: () => api.unwrap(api.client.DELETE("/v1/admin/uploads/{id}", { params: { path: { id: u.id } } })),
    onSuccess: refresh,
  });

  const resume = async (file: File | undefined) => {
    if (!file) return;
    const fresh = await api.unwrap(api.client.GET("/v1/admin/uploads/{id}", { params: { path: { id: u.id } } }));
    setResumeError(uploadQueue.resume(fresh, file));
  };

  const pct = Math.round(u.progress * 100);
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <StatusIcon status={u.status} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{u.lesson_title}</p>
        <p className="truncate text-xs text-muted">
          {u.course_title} › {u.module_title} · {u.filename} · {bytes(u.size)} · {relative(u.created_at)}
        </p>
        {u.status === "processing" && <ProgressBar value={pct} className="mt-2 max-w-md" />}
        {u.error && u.status === "failed" && <p className="mt-1 text-xs text-danger">{u.error}</p>}
        {resumeError && <p className="mt-1 text-xs text-danger">{resumeError}</p>}
        {(retry.error || cancel.error) && (
          <p className="mt-1 text-xs text-danger">{errorMessage(retry.error ?? cancel.error)}</p>
        )}
      </div>
      <span className="text-xs text-muted">{statusLabel(u, sendingHere)}</span>
      <div className="flex gap-2">
        {u.status === "done" && u.lesson_id && (
          <Link to="/aula/$lessonId" params={{ lessonId: u.lesson_id }} className="btn-ghost px-3 py-1">
            <Play className="h-3.5 w-3.5" /> Assistir
          </Link>
        )}
        {u.status === "uploading" && !sendingHere && (
          <>
            <button className="btn-ghost px-3 py-1" onClick={() => pick.current?.click()}>
              Continuar envio
            </button>
            <input
              ref={pick}
              type="file"
              accept={VIDEO_ACCEPT}
              className="hidden"
              aria-label={`Escolher ${u.filename} para continuar`}
              onChange={(e) => {
                void resume(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </>
        )}
        {u.status === "failed" && (
          <button className="btn-ghost px-3 py-1" disabled={retry.isPending} onClick={() => retry.mutate()}>
            <RotateCcw className="h-3.5 w-3.5" /> Tentar de novo
          </button>
        )}
        {(u.status === "failed" || u.status === "queued" || (u.status === "uploading" && !sendingHere)) && (
          <button
            className="rounded p-1 text-muted hover:text-danger"
            aria-label="Cancelar envio"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </li>
  );
}

function StatusIcon({ status }: { status: Upload["status"] }) {
  if (status === "done") return <CheckCircle2 className="h-5 w-5 shrink-0 text-ok" aria-hidden />;
  if (status === "failed") return <X className="h-5 w-5 shrink-0 text-danger" aria-hidden />;
  if (status === "canceled") return <X className="h-5 w-5 shrink-0 text-muted" aria-hidden />;
  return <Loader2 className="h-5 w-5 shrink-0 animate-spin text-accent" aria-hidden />;
}

function statusLabel(u: Upload, sendingHere: boolean): string {
  switch (u.status) {
    case "uploading":
      return sendingHere
        ? "Enviando…"
        : `Interrompido (${Math.round((u.parts_done.length / Math.max(u.parts_total, 1)) * 100)}% enviado)`;
    case "queued":
      return "Na fila";
    case "processing":
      return `${capitalize(u.stage || "processando")} · ${Math.round(u.progress * 100)}%`;
    case "done":
      return "Pronto";
    case "failed":
      return "Falhou";
    case "canceled":
      return "Cancelado";
  }
}

// ------------------------------------------------------------ apoio

/** Avisa antes de fechar a aba com envio em andamento. */
function useLeaveWarning() {
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent) => {
      if (uploadQueue.busy) e.preventDefault();
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, []);
}

export function bytes(n: number): string {
  if (n >= 1 << 30) return `${(n / (1 << 30)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} GB`;
  if (n >= 1 << 20) return `${Math.round(n / (1 << 20)).toLocaleString("pt-BR")} MB`;
  return `${Math.max(1, Math.round(n / 1024)).toLocaleString("pt-BR")} KB`;
}

const capitalize = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
